import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { clientApi } from '../../services/api';
import type { Client, PaginatedResponse } from '../../types';
import {
  UserPlusIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon
} from '@heroicons/react/24/outline';

const ClientList: React.FC = () => {
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState(''); // Valor do input (atualiza imediatamente)
  const [filters, setFilters] = useState({
    person_type: '',
    client_type: '',
    is_active: 'true',  // Filtrar apenas ativos por padrão
    city: '',
    state: ''
  });
  const [pagination, setPagination] = useState({
    count: 0,
    next: null as string | null,
    previous: null as string | null,
    current_page: 1
  });
  const [pageSize, setPageSize] = useState(15);

  const loadClients = async (page = 1, size = pageSize) => {
    try {
      setLoading(true);
      // Build params object with proper types
      const params: {
        page?: number;
        page_size?: number;
        search?: string;
        person_type?: string;
        client_type?: string;
        is_active?: boolean;
        city?: string;
        state?: string;
        ordering?: string;
      } = {
        page,
        page_size: size,
        search: searchTerm,
        ordering: '-created_at'
      };

      // Add filters if they have values
      if (filters.person_type) {
        params.person_type = filters.person_type;
      }
      if (filters.client_type) {
        params.client_type = filters.client_type;
      }
      if (filters.is_active) {
        params.is_active = filters.is_active === 'true';
      }
      if (filters.city) {
        params.city = filters.city;
      }
      if (filters.state) {
        params.state = filters.state;
      }

      const response: PaginatedResponse<Client> = await clientApi.getClients(params);
      setClients(response.results);
      setPagination({
        count: response.count,
        next: response.next,
        previous: response.previous,
        current_page: page
      });
    } catch (error) {
      console.error('Error loading clients:', error);
    } finally {
      setLoading(false);
    }
  };

  // Debounce para busca - só busca com 3+ caracteres ou se estiver vazio
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput.length === 0 || searchInput.length >= 3) {
        setSearchTerm(searchInput);
      }
    }, 500); // 500ms de debounce

    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    loadClients(1, pageSize);
  }, [searchTerm, filters, pageSize]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e.target.value);
  };

  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleDeleteClient = async (clientId: string) => {
    if (window.confirm('Tem certeza que deseja excluir este cliente?')) {
      try {
        await clientApi.deleteClient(clientId);
        loadClients(pagination.current_page, pageSize);
      } catch (error) {
        console.error('Error deleting client:', error);
      }
    }
  };

  const getPersonTypeLabel = (type: string) => {
    return type === 'PF' ? 'PF' : 'PJ';
  };

  const getClientTypeLabel = (type?: string) => {
    switch (type) {
      case 'customer': return 'Cliente';
      case 'supplier': return 'Fornecedor';
      case 'both': return 'Ambos';
      default: return 'Cliente';
    }
  };

  const getClientTypeBadge = (type?: string) => {
    switch (type) {
      case 'supplier':
        return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">Fornecedor</span>;
      case 'both':
        return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">Ambos</span>;
      default:
        return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">Cliente</span>;
    }
  };

  const getStatusBadge = (isActive: boolean) => {
    return isActive ? (
      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
        Ativo
      </span>
    ) : (
      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
        Inativo
      </span>
    );
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-base font-bold text-secondary-900">Clientes</h1>
          <p className="text-xs text-secondary-600">
            Gerencie todos os clientes cadastrados no sistema
          </p>
        </div>
        <Link to="/clients/new" className="btn-primary flex items-center whitespace-nowrap">
          <UserPlusIcon className="h-4 w-4 mr-1.5" />
          Novo Cliente
        </Link>
      </div>

      {/* Search and Filters */}
      <div className="card">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-secondary-400" />
              <input
                type="text"
                placeholder="Buscar clientes (mín. 3 caracteres)..."
                className="input-field pl-10"
                value={searchInput}
                onChange={handleSearch}
              />
            </div>
          </div>

          {/* Filters */}
          <div className="flex gap-4">
            <select
              className="input-field"
              value={filters.person_type}
              onChange={(e) => handleFilterChange('person_type', e.target.value)}
            >
              <option value="">Todos os tipos</option>
              <option value="PF">Pessoa Física</option>
              <option value="PJ">Pessoa Jurídica</option>
            </select>

            <select
              className="input-field"
              value={filters.client_type}
              onChange={(e) => handleFilterChange('client_type', e.target.value)}
            >
              <option value="">Todos</option>
              <option value="customer">Clientes</option>
              <option value="supplier">Fornecedores</option>
              <option value="both">Ambos</option>
            </select>

            <select
              className="input-field"
              value={filters.is_active}
              onChange={(e) => handleFilterChange('is_active', e.target.value)}
            >
              <option value="">Todos os status</option>
              <option value="true">Ativo</option>
              <option value="false">Inativo</option>
            </select>
          </div>
        </div>
      </div>

      {/* Results */}
      <div className="card">
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-2 text-secondary-600">Carregando clientes...</p>
          </div>
        ) : clients.length === 0 ? (
          <div className="text-center py-8">
            <UserPlusIcon className="h-12 w-12 text-secondary-400 mx-auto" />
            <p className="mt-2 text-secondary-600">Nenhum cliente encontrado</p>
          </div>
        ) : (
          <>
            {/* Responsive Cards for Mobile */}
            <div className="block lg:hidden space-y-3">
              {clients.map((client) => (
                <div key={client.id} className="border border-secondary-200 rounded-lg p-3 bg-white">
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex-1">
                      <h3 className="text-sm font-medium text-secondary-900">{client.name}</h3>
                      <p className="text-xs text-secondary-500">{client.email}</p>
                    </div>
                    {getStatusBadge(client.is_active)}
                  </div>
                  
                  <div className="grid grid-cols-2 gap-2 text-xs text-secondary-600 mb-2">
                    <div><span className="font-medium">Tipo:</span> {getPersonTypeLabel(client.person_type)}</div>
                    <div><span className="font-medium">Doc:</span> {client.cpf_cnpj}</div>
                    <div><span className="font-medium">Contato:</span> {client.signatory_name || '-'}</div>
                    <div><span className="font-medium">Tel:</span> {client.signatory_phone || '-'}</div>
                  </div>
                  
                  <div className="flex justify-end space-x-2">
                    <Link to={`/clients/${client.id}`} className="text-primary-600 hover:text-primary-900 p-1" title="Visualizar">
                      <EyeIcon className="h-4 w-4" />
                    </Link>
                    <Link to={`/clients/${client.id}/edit`} className="text-yellow-600 hover:text-yellow-900 p-1" title="Editar">
                      <PencilIcon className="h-4 w-4" />
                    </Link>
                    <button onClick={() => handleDeleteClient(client.id)} className="text-red-600 hover:text-red-900 p-1" title="Excluir">
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Table for Desktop */}
            <div className="hidden lg:block overflow-x-auto">
              <table className="min-w-full">
                <thead className="bg-secondary-50">
                  <tr>
                    <th className="px-2 py-1.5 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">Nome</th>
                    <th className="px-2 py-1.5 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">Tipo</th>
                    <th className="px-2 py-1.5 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">Relacao</th>
                    <th className="px-2 py-1.5 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">Documento</th>
                    <th className="px-2 py-1.5 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">Local</th>
                    <th className="px-2 py-1.5 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">Status</th>
                    <th className="px-2 py-1.5 text-right text-xs font-medium text-secondary-500 uppercase tracking-wider">Acoes</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-secondary-100">
                  {clients.map((client) => (
                    <tr key={client.id} className="hover:bg-secondary-25">
                      <td className="px-2 py-1.5">
                        <div className="text-xs font-medium text-secondary-900 truncate max-w-32">{client.name}</div>
                        <div className="text-xs text-secondary-500 truncate max-w-32">{client.email}</div>
                      </td>
                      <td className="px-2 py-1.5 text-xs text-secondary-900">{getPersonTypeLabel(client.person_type)}</td>
                      <td className="px-2 py-1.5">{getClientTypeBadge(client.client_type)}</td>
                      <td className="px-2 py-1.5 text-xs text-secondary-900">{client.cpf_cnpj}</td>
                      <td className="px-2 py-1.5 text-xs text-secondary-900">{client.city && client.state ? `${client.city}/${client.state}` : '-'}</td>
                      <td className="px-2 py-1.5">{getStatusBadge(client.is_active)}</td>
                      <td className="px-2 py-1.5 text-right">
                        <div className="flex justify-end space-x-1">
                          <Link to={`/clients/${client.id}`} className="text-primary-600 hover:text-primary-900 p-0.5" title="Visualizar">
                            <EyeIcon className="h-3.5 w-3.5" />
                          </Link>
                          <Link to={`/clients/${client.id}/edit`} className="text-yellow-600 hover:text-yellow-900 p-0.5" title="Editar">
                            <PencilIcon className="h-3.5 w-3.5" />
                          </Link>
                          <button onClick={() => handleDeleteClient(client.id)} className="text-red-600 hover:text-red-900 p-0.5" title="Excluir">
                            <TrashIcon className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.count > pageSize && (
              <div className="flex items-center justify-between border-t border-secondary-200 bg-white px-4 py-3 sm:px-6">
                <div className="flex flex-1 justify-between sm:hidden">
                  <button
                    onClick={() => loadClients(pagination.current_page - 1, pageSize)}
                    disabled={!pagination.previous}
                    className="relative inline-flex items-center rounded-md border border-secondary-300 bg-white px-3 py-1.5 text-sm font-medium text-secondary-700 hover:bg-secondary-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Anterior
                  </button>
                  <button
                    onClick={() => loadClients(pagination.current_page + 1, pageSize)}
                    disabled={!pagination.next}
                    className="relative ml-3 inline-flex items-center rounded-md border border-secondary-300 bg-white px-3 py-1.5 text-sm font-medium text-secondary-700 hover:bg-secondary-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Próximo
                  </button>
                </div>
                <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                  <div className="flex items-center space-x-4">
                    <p className="text-xs text-secondary-700">
                      Mostrando{' '}
                      <span className="font-medium">
                        {(pagination.current_page - 1) * pageSize + 1}
                      </span>{' '}
                      até{' '}
                      <span className="font-medium">
                        {Math.min(pagination.current_page * pageSize, pagination.count)}
                      </span>{' '}
                      de{' '}
                      <span className="font-medium">{pagination.count}</span>{' '}
                      resultados
                    </p>
                    <div className="flex items-center space-x-2">
                      <label className="text-sm text-secondary-700">Por página:</label>
                      <select
                        value={pageSize}
                        onChange={(e) => {
                          const newSize = parseInt(e.target.value);
                          setPageSize(newSize);
                          loadClients(1, newSize);
                        }}
                        className="text-sm border border-secondary-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value={10}>10</option>
                        <option value={15}>15</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                      <button
                        onClick={() => loadClients(pagination.current_page - 1, pageSize)}
                        disabled={!pagination.previous}
                        className="relative inline-flex items-center rounded-l-md px-2 py-2 text-secondary-400 ring-1 ring-inset ring-secondary-300 hover:bg-secondary-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <span className="sr-only">Anterior</span>
                        ←
                      </button>
                      <button
                        onClick={() => loadClients(pagination.current_page + 1, pageSize)}
                        disabled={!pagination.next}
                        className="relative inline-flex items-center rounded-r-md px-2 py-2 text-secondary-400 ring-1 ring-inset ring-secondary-300 hover:bg-secondary-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <span className="sr-only">Próximo</span>
                        →
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ClientList;